import Appointment, { status as AppointmentStatusIds } from '@spike/appointment-model';

import { WorkflowStepAction } from '@spike/masterdata-model';

import { Option } from '@spike/model';

import moment, { Moment } from 'moment-timezone';

export const COMPLETED_APPOINTMENT_STATUS_ID = AppointmentStatusIds.READY_FOR_CHECK_OUT;

export const CLOSED_APPOINTMENT_STATUS_ID = AppointmentStatusIds.COMPLETED;

export const isCompletedStatus = (statusId: number): boolean => statusId === COMPLETED_APPOINTMENT_STATUS_ID;

export const isClosedStatus = (statusId: number): boolean => statusId === CLOSED_APPOINTMENT_STATUS_ID;

export const isRestStatus = (statusId: number): boolean => !isCompletedStatus(statusId) && !isClosedStatus(statusId);

export const CLOSED_ORDER_EDIT_WINDOW_DAYS = 30;

export const isWithinClosedEditWindow = (closedAt: Moment | undefined, now: Moment = moment()): boolean => {
    if (!closedAt) {
        return false;
    }

    return now.diff(closedAt, 'days') <= CLOSED_ORDER_EDIT_WINDOW_DAYS;
};

// --- Status transitions ---

export interface AppointmentTransitionContext {
    currentStatusId: number;

    isOwner: boolean;

    reopenClosedBookings: boolean;

    /** Required for closed (6) → completed (5); uses the same 30-day window as order edit. */

    closedAt?: Moment;

    now?: Moment;
}

export const canManageClosedAppointmentStatus = (context: AppointmentTransitionContext): boolean =>
    context.isOwner || context.reopenClosedBookings;

export const isTransitionAllowed = (
    fromStatusId: number,

    toStatusId: number,

    context: AppointmentTransitionContext
): boolean => {
    const canManageClosed = canManageClosedAppointmentStatus(context);

    if (isCompletedStatus(fromStatusId) || isClosedStatus(fromStatusId)) {
        if (toStatusId === AppointmentStatusIds.CANCELLED || toStatusId === AppointmentStatusIds.NO_SHOW) {
            return false;
        }
    }

    if (isRestStatus(fromStatusId)) {
        if (toStatusId === AppointmentStatusIds.CANCELLED || toStatusId === AppointmentStatusIds.NO_SHOW) {
            return true;
        }

        if (isCompletedStatus(toStatusId)) {
            return true;
        }

        return isRestStatus(toStatusId);
    }

    if (isCompletedStatus(fromStatusId)) {
        return isClosedStatus(toStatusId) && canManageClosed;
    }

    if (isClosedStatus(fromStatusId)) {
        if (!isCompletedStatus(toStatusId) || !canManageClosed) {
            return false;
        }

        return isWithinClosedEditWindow(context.closedAt, context.now);
    }

    return false;
};

export const filterAllowedStatusActions = (
    actions: WorkflowStepAction[],

    context: AppointmentTransitionContext
): WorkflowStepAction[] =>
    actions.filter(
        action => action.show && isTransitionAllowed(context.currentStatusId, action.nextStatus.id, context)
    );

export const appendMissingClosedTransitionActions = (
    actions: WorkflowStepAction[],

    context: AppointmentTransitionContext,

    statusOptions: Option<number>[]
): WorkflowStepAction[] => {
    if (!canManageClosedAppointmentStatus(context)) {
        return actions;
    }

    const result = [...actions];

    if (isClosedStatus(context.currentStatusId)) {
        const targetId = COMPLETED_APPOINTMENT_STATUS_ID;

        if (
            !result.some(action => action.nextStatus.id === targetId) &&
            isTransitionAllowed(context.currentStatusId, targetId, context)
        ) {
            const nextStatus = statusOptions.find(status => status.id === targetId);

            if (nextStatus) {
                result.push({ action: 'change_status', nextStatus, show: true });
            }
        }
    }

    if (isCompletedStatus(context.currentStatusId)) {
        const targetId = CLOSED_APPOINTMENT_STATUS_ID;

        if (
            !result.some(action => action.nextStatus.id === targetId) &&
            isTransitionAllowed(context.currentStatusId, targetId, context)
        ) {
            const nextStatus = statusOptions.find(status => status.id === targetId);

            if (nextStatus) {
                result.push({ action: 'change_status', nextStatus, show: true });
            }
        }
    }

    return result;
};

// --- Order item edit / add ---

export const getOrderClosedAtFromAppointments = (appointments: Appointment[]): Moment | undefined =>
    appointments

        .filter(appointment => isClosedStatus(appointment.status.id))

        .map(appointment => appointment.closedAt)

        .filter((closedAt): closedAt is Moment => closedAt != null)

        .reduce<Moment | undefined>(
            (latest, closedAt) => (!latest || closedAt.isAfter(latest) ? closedAt : latest),

            undefined
        );

export interface OrderEditContext {
    isOwner: boolean;

    reopenClosedBookings: boolean;

    editPrices: boolean;
}

export const isOrderFullyClosed = (appointmentStatusIds: number[]): boolean =>
    appointmentStatusIds.length > 0 && appointmentStatusIds.every(isClosedStatus);

export const canManageClosedOrder = (context: OrderEditContext): boolean =>
    context.isOwner || context.reopenClosedBookings;

export const canAddToOrder = (
    appointmentStatusIds: number[],

    context: OrderEditContext,

    orderClosedAt?: Moment,

    now?: Moment
): boolean => {
    if (!isOrderFullyClosed(appointmentStatusIds)) {
        return true;
    }

    if (!canManageClosedOrder(context)) {
        return false;
    }

    return isWithinClosedEditWindow(orderClosedAt, now);
};

export const canEditAppointmentItems = (
    statusId: number,

    context: OrderEditContext,

    closedAt?: Moment,

    now?: Moment
): boolean => {
    if (isClosedStatus(statusId)) {
        if (!canManageClosedOrder(context)) {
            return false;
        }

        return isWithinClosedEditWindow(closedAt, now);
    }

    if (isCompletedStatus(statusId)) {
        return context.editPrices;
    }

    if (isRestStatus(statusId)) {
        return true;
    }

    return false;
};
